"use client";

import type { ReviewItem } from "@/lib/api/projection-types";
import type { ContentQueueItem } from "@/lib/api";
import { LIFE_AREA_META } from "@/lib/lifeAreas";
import {
  formatTimeAgo,
  libraryTypeKey,
  LIBRARY_TYPE_LABEL,
} from "@/lib/libraryFormatters";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import LibraryTypeIcon from "./LibraryTypeIcon";

interface Props {
  item: ReviewItem;
  detail?: ContentQueueItem;
  recommended: boolean;
  selected: boolean;
  selecting: boolean;
  busy: boolean;
  onToggleSelect: (id: string) => void;
  onOpenDetail: (id: string) => void;
  onAddToTodo: (item: ReviewItem) => void;
  onAddToSchedule: (item: ReviewItem) => void;
}

export default function LibraryListRow({
  item,
  detail,
  recommended,
  selected,
  selecting,
  busy,
  onToggleSelect,
  onOpenDetail,
  onAddToTodo,
  onAddToSchedule,
}: Props) {
  const title = sanitizeSurfaceTitle(item.title);
  const areaMeta = item.goal_category ? LIFE_AREA_META[item.goal_category] : null;
  const typeKey = libraryTypeKey(item.content_type ?? detail?.content_type);
  const timeAgo = formatTimeAgo(detail?.saved_at ?? null);

  const baseClass = recommended
    ? "border-amber/50 bg-sand-pale"
    : "border-border bg-card";
  const selectedClass = selected
    ? "ring-2 ring-forest-med"
    : "hover:bg-bg";

  return (
    <div
      data-testid={recommended ? "library-row-recommended" : "library-row"}
      data-reveal-id={item.id}
      className={`group grid cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 transition-colors ${baseClass} ${selectedClass}`}
      style={{ gridTemplateColumns: "20px 24px minmax(0,1fr) auto auto" }}
      onClick={() => onToggleSelect(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleSelect(item.id);
        }
      }}
      aria-pressed={selected}
    >
      <button
        type="button"
        aria-pressed={selected}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(item.id);
        }}
        className={`flex h-[18px] w-[18px] items-center justify-center rounded transition-opacity ${
          selected
            ? "bg-forest-med text-white opacity-100"
            : "border border-border bg-white text-transparent group-hover:opacity-100"
        } ${selecting || selected ? "opacity-100" : "opacity-0"}`}
      >
        {selected && (
          <svg width="11" height="11" viewBox="0 0 12 12">
            <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <div
        aria-hidden
        className={`flex h-7 w-7 items-center justify-center rounded-md ${
          recommended ? "border border-amber/40 bg-white/70" : "border border-border bg-bg"
        } text-text-light`}
      >
        <LibraryTypeIcon type={typeKey} size={14} />
      </div>

      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium text-text">{title}</div>
        <div className="truncate text-[11px] text-muted">
          {areaMeta && (
            <>
              <span className="capitalize text-forest-med">
                {areaMeta.emoji} {areaMeta.label}
              </span>
              <span> · </span>
            </>
          )}
          <span className="capitalize">{LIBRARY_TYPE_LABEL[typeKey]}</span>
          {recommended && (
            <>
              <span> · </span>
              <span className="font-semibold text-earth">Recommended</span>
            </>
          )}
        </div>
      </div>

      <span className="text-[11px] tabular-nums text-muted" aria-label="Saved">
        {timeAgo || "—"}
      </span>

      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        <RowAction
          label="Add to todo"
          onClick={(e) => {
            e.stopPropagation();
            onAddToTodo(item);
          }}
          disabled={busy}
        >
          <svg width="14" height="14" viewBox="0 0 14 14">
            <rect x="2" y="2" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M5 7l1.5 1.5L9 6" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </RowAction>
        <RowAction
          label="Add to schedule"
          onClick={(e) => {
            e.stopPropagation();
            onAddToSchedule(item);
          }}
          disabled={busy}
          testId="review-place-schedule"
        >
          <svg width="14" height="14" viewBox="0 0 14 14">
            <rect x="2" y="3" width="10" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M2 6h10M5 2v2M9 2v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </RowAction>
        <RowAction
          label="Open details"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetail(item.id);
          }}
          disabled={false}
        >
          <svg width="14" height="14" viewBox="0 0 14 14">
            <path d="M5 3h6v6M11 3l-7 7" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round" />
          </svg>
        </RowAction>
      </div>
    </div>
  );
}

function RowAction({
  label,
  onClick,
  disabled,
  children,
  testId,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  disabled: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex h-7 w-7 items-center justify-center rounded-full text-text-light transition-colors hover:bg-secondary hover:text-forest-med disabled:opacity-60"
    >
      {children}
    </button>
  );
}
